import { useCallback, useMemo, useRef } from 'react';
import store, { showToast } from '../../../../core/store';
import { updateCasePlanWidgetConfig } from '../../api/casePlanDetailApi';
import { CASE_PLAN_WIDGET_ERROR_MESSAGES } from '../../constants/reentry-planning.constants';
import { findMappedWidgetById } from '../../utils/reentry-plan-detail.util';
import { replaceNoteWidgetProps, toNoteWidgetConfig } from './note-widget.factory';

/**
 * Persists note body edits via widget config PATCH.
 * Updates local section state immediately; rolls back and toasts if the PATCH fails.
 *
 * @param {{
 *   casePlanId: string,
 *   sections: object[],
 *   setSections: (updater: object[] | ((current: object[]) => object[])) => void,
 * }} params
 */
export const useNoteWidgetMutations = ({ casePlanId, sections, setSections }) => {
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;

  const onSave = useCallback(
    async (widgetId, body) => {
      if (!casePlanId || !widgetId) {
        return;
      }

      const currentProps = findMappedWidgetById(sectionsRef.current, widgetId)?.props ?? {};
      const previousProps = { ...currentProps };
      const nextProps = { ...currentProps, body };

      setSections((current) => replaceNoteWidgetProps(current, widgetId, nextProps));

      try {
        await updateCasePlanWidgetConfig(casePlanId, widgetId, toNoteWidgetConfig(nextProps));
      } catch {
        setSections((current) => replaceNoteWidgetProps(current, widgetId, previousProps));
        store.dispatch(showToast(CASE_PLAN_WIDGET_ERROR_MESSAGES.UPDATE_CONFIG));
      }
    },
    [casePlanId, setSections],
  );

  return useMemo(() => ({ onSave }), [onSave]);
};
